/**
 * Typed-Endpoint-Module — gruppiert nach Backend-Resource. Jeder Aufruf gibt typisierte
 * Responses zurück; Errors werden vom HTTP-Client geworfen (HTTPError) und mit
 * `toApiError(err)` in ein UI-Shape umgewandelt.
 *
 * Architektur-Regel: KEINE Geschäftslogik hier — nur Roh-Typed-Calls. Mapping
 * (z. B. "wie viele Properties hat dieser Tenant?") gehört in `features/<feature>/hooks/`.
 */
import type { KyInstance } from 'ky';
import type { Session } from '@stayon/auth-core';
import type {
  AriBatchPushQueuedResult,
  AriPushBatchRequest,
  AriPushQueuedResult,
  AriOperationStatusResponse,
  AriOwnershipImpact,
  AriOwnershipReleaseRequest,
  AriOwnershipReleaseResult,
  AriPushRequest,
  FullSyncPreview,
  AvailabilityResult,
  AvailabilityDistributionCalendarResponse,
  ChannelCloseoutAccepted,
  ChannelCloseoutActionRequest,
  ChannelCloseoutDetail,
  ChannelCloseoutOptions,
  ChannelCloseoutPage,
  ChannelCloseoutPreview,
  ChannelCloseoutUpdateRequest,
  ChannelCloseoutWriteRequest,
  BookingListQuery,
  BookingResponse,
  BookingCancellationPreviewResponse,
  BookingCancellationResponse,
  BookingReassignmentPreviewResponse,
  BookingReassignmentResponse,
  CancelEarlyDeparturePlanRequest,
  CreateBookingCancellationRequest,
  CreateBookingReassignmentRequest,
  CreateUpgradeChargeRequest,
  CreateOtaEarlyDepartureRequest,
  EarlyDeparturePlanSummary,
  OtaEarlyDepartureResponse,
  UpsertEarlyDeparturePlanRequest,
  UpdateBookingRequest,
  UpgradeChargeResponse,
  UpdateWorkspaceTwoFactorPolicyRequest,
  ResetUserTwoFactorRequest,
  PlatformAdminChangeRequest,
  WorkspaceTwoFactorPolicyResponse,
  CalendarResponse,
  DynamicPricingCalendarResponse,
  DynamicPricingCalendarDayDetailResponse,
  OnboardingSupportResponse,
  AriPushHistoryResponse,
  AriStateResponse,
  ChannexChannelResource,
  LastBookingsPerOtaResponse,
  SyncActivityResponse,
  SyncActivityDetails,
  ChannexConnectionsResponse,
  ChannexIframeSession,
  ChannexProvisioningResult,
  ChannexPublicationResponse,
  ChannexRevisionHoldList,
  ChannexQuarantineRederiveResult,
  ChannexRevisionReplayResult,
  ChannexReconcileAction,
  ChannexUnitResource,
  ChannexWebhookEventResponse,
  AssignCleaningTaskRequest,
  ChecklistTemplateResponse,
  CleaningAssigneeResponse,
  CleaningCompletionReportFilter,
  CleaningCompletionReportResponse,
  CleaningPhotoResponse,
  CleaningRangeSummaryResponse,
  CleaningScopeResponse,
  CleaningTaskDetailResponse,
  CleaningTaskQueueResponse,
  CleaningTaskResponse,
  CleaningTaskStatus,
  CompleteCleaningTaskRequest,
  CreateChecklistTemplateRequest,
  CreateCleaningTaskRequest,
  AuditLogFilter,
  AuditLogResponse,
  OutboxDeadLetterItem,
  ConsentLogAdminResponse,
  CompleteOtaFinanceDataRequest,
  CreateFinanceBookingManualDecisionRequest,
  ExpenseListItem,
  CreateFinancePositionManualDecisionRequest,
  FinanceOverviewQuery,
  FinanceOverviewResponse,
  FinanceJournalEventDetail,
  FinanceJournalEventsPage,
  FinanceJournalEventsQuery,
  FinanceBookingDetail,
  FinanceBookingFeed,
  FinanceBookingQuery,
  FinanceAnalyticsQuery,
  FinanceAnalyticsResponse,
  FinanceFeeEvidenceFeed,
  FinanceFeeEvidenceCard,
  FinanceFeeEvidenceQuery,
  FinanceMonthlyQuery,
  FinanceMonthlyResponse,
  FinancialAutomationCaseResponse,
  FinancialAutomationCutoverResponse,
  FinancialAutomationDecisionRequest,
  FinancialAutomationWorkResponse,
  OtaBookingRefundLedger,
  OtaBookingRefundResponse,
  CreateOtaBookingRefundRequest,
  CompensateOtaBookingRefundRequest,
  GuestProfileResponse,
  ConversationDetail,
  ConversationListItem,
  CursorPage,
  MessageItem,
  MessagingAccessInfo,
  MessagingApartmentCard,
  MessagingReplyRequest,
  MessageSendOperationDetail,
  BookingMessageOperationResponse,
  MessageSendResolutionRequest,
  MessageSendResolutionResponse,
  UnreadSummary,
  InsightsResponse,
  InvoiceListItem,
  InvoiceListQuery,
  InvoiceRecipientReplacementRequest,
  InvoiceResponse,
  PnLResponse,
  PortfolioQuery,
  PortfolioResponse,
  TaxSettingsResponse,
  UpdateTaxSettingsRequest,
  StripeDoctorResult,
  StripeSettingsResponse,
  UpdateStripeSettingsRequest,
  CreateParkingBlockRequest,
  CreateMappedParkingReservationRequest,
  MappedParkingReservationResponse,
  ParkingBatchResult,
  ParkingBlockResponse,
  ParkingBookingSuggestionsResponse,
  ParkingOccupancyResponse,
  ParkingSpotListResponse,
  ParkingSpotResponse,
  SaveParkingSpotRequest,
  BookableResourceListResponse,
  BookableResourceResponse,
  SaveBookableResourceRequest,
  CreateResourceBlockRequest,
  ResourceImageListResponse,
  ResourceImageResponse,
  ResourceOccupancyResponse,
  ResourceReservationResponse,
  OnboardingLinkAdminResponse,
  OnboardingShareLinkResponse,
  PendingOnboardingResponse,
  OutboxFilterStatus,
  OutboxMessageResponse,
  PropertyFileListFilter,
  PropertyFileResponse,
  PropertyFileTreeNode,
  MovePropertyFileRequest,
  ReclassifyPropertyFileRequest,
  CreateFolderRequest,
  RenameFolderRequest,
  PropertyFolderResponse,
  TrashedFileResponse,
  UploadPropertyFileRequest,
  ReportDamageRequest,
  ReportMissingItemRequest,
  UpdateChecklistTemplateRequest,
  UpdatePropertyFileMetadataRequest,
  CreatePropertyRequest,
  CreateUserRequest,
  CompanyResponse,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  PropertyAssignmentResponse,
  CreatePropertyAssignmentRequest,
  CleaningTeamResponse,
  CreateCleaningTeamRequest,
  UpdateCleaningTeamRequest,
  InvoiceTemplateResponse,
  CreateInvoiceTemplateRequest,
  UpdateInvoiceTemplateRequest,
  AssignInvoiceTemplateRequest,
  InvoiceBlockResponse,
  CreateInvoiceBlockRequest,
  UpdateInvoiceBlockRequest,
  ReorderInvoiceBlocksRequest,
  InvoiceItemPresetResponse,
  InvoiceDraftResponse,
  CreateInvoiceDraftRequest,
  UpdateInvoiceDraftRequest,
  CreateInvoiceItemPresetRequest,
  UpdateInvoiceItemPresetRequest,
  ReorderInvoiceItemPresetsRequest,
  DashboardKpisResponse,
  RevenueTrendResponse,
  OperationsDayResponse,
  ActivateAccountRequest,
  ForgotPasswordRequest,
  LoginRequest,
  CsrfTokenResponse,
  AuthLoginResponse,
  CompleteTwoFactorLoginRequest,
  ManageTwoFactorRequest,
  TwoFactorSetupCompletionResponse,
  RequiredTwoFactorSetupConfirmationResponse,
  TwoFactorSetupResponse,
  TwoFactorStatusResponse,
  CreateManualBookingRequest,
  PagedResult,
  BookingListResponse,
  PropertyResponse,
  PropertyArchivePreflightResponse,
  PropertyArchiveResponse,
  PropertyGroupResponse,
  GuestExperienceSettingsResponse,
  UpdateGuestExperienceSettingsRequest,
  GuestExperiencePreviewRequest,
  GuestExperiencePreviewResponse,
  PropertyCommunicationCatalogResponse,
  PropertyCommunicationPreviewRequest,
  PropertyCommunicationPreviewResponse,
  UpdatePropertyCommunicationCatalogRequest,
  PropertyPortalBrandingResponse,
  UpdatePropertyPortalBrandingRequest,
  AddressSuggestionResponse,
  AddressSearchRequest,
  MergePropertyGroupsRequest,
  SplitPropertyGroupRequest,
  UpsertPropertyGroupRequest,
  QueryRequest,
  PropertyListQuery,
  ResetPasswordRequest,
  UpdatePropertyRequest,
  UpdatePropertyStayInfoRequest,
  UpdatePropertyCleaningCapacityRequest,
  ArrivalStepKind,
  ArrivalStepListResponse,
  ArrivalStepResponse,
  UpsertArrivalStepRequest,
  DeleteArrivalStepResponse,
  ArrivalStepPreviewRequest,
  ArrivalStepPreviewResponse,
  ArrivalAnnotation,
  ArrivalImageListResponse,
  ArrivalImageResponse,
  PropertyAccessMappingResponse,
  SetStaticAccessCodeRequest,
  UpdateUserRequest,
  UserListQuery,
  UserResponse,
  UserAccessResponse,
  UserAccessPreviewResponse,
  UpdateUserAccessRequest,
  ContractResponse,
  CreateContractRequest,
  UpdateContractRequest,
  ContractListFilter,
  SupplierInvoiceResponse,
  CreateSupplierInvoiceRequest,
  UpdateSupplierInvoiceRequest,
  SupplierInvoiceListFilter,
  MarkInvoicePaidRequest,
  QuickSearchRequest,
  QuickSearchResponse,
  SearchCatalogResponse,
  AdvancedSearchRequest,
  AdvancedSearchResponse,
  SearchExportFormat,
  PricingConfigurationInput,
  PricingConfigurationPreviewRequest,
  PricingConfigurationPreviewResponse,
  PricingConfigurationResponse,
  PricingConfigurationValidationResponse,
  PricingStrategyProfileResponse,
  DynamicPricingOverviewResponse,
  DynamicPricingHistoryQuery,
  DynamicPricingHistoryResponse,
  UpdateDynamicPricingTenantControlRequest,
  DynamicPricingTenantControlResponse,
  ChannelPriceModifierItem,
  ChannelPriceModifierChangeAccepted,
  ChannelPriceModifierPreview,
  UpdateChannelPriceModifierRequest,
  UpdatePricingConfigurationRequest,
  SavedSearchSummary,
  SavedSearchDetail,
  SaveSearchRequest,
  RunSavedSearchRequest,
  WorkspaceSummaryResponse,
  CreateWorkspaceRequest,
  WorkspaceResponse,
  WorkspaceSettingsResponse,
  UpdateWorkspaceSettingsRequest,
} from './types';
import { attachmentFileName } from './attachmentFileName';
import { buildSupportSessionApi } from './supportSessionEndpoints';
import { buildWholeFlatApi } from './wholeFlatEndpoints';
import { buildPluginApi } from './pluginEndpoints';
import { buildAutomationControlApi } from './automationControlEndpoints';
import { buildPluginCasesApi } from './pluginCasesEndpoints';

function toQuery(q: QueryRequest): URLSearchParams {
  const params = new URLSearchParams();
  if (q.page !== undefined) params.set('page', String(q.page));
  if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
  if (q.sort !== undefined) params.set('sort', q.sort);
  if (q.search !== undefined) params.set('search', q.search);
  appendPropertyIds(params, q.propertyIds);
  return params;
}

const EMPTY_SCOPE_ID = '00000000-0000-0000-0000-000000000000';
function appendPropertyIds(params: URLSearchParams, propertyIds?: string[] | null) {
  if (propertyIds === null || propertyIds === undefined) return;
  (propertyIds.length > 0 ? propertyIds : [EMPTY_SCOPE_ID]).forEach((id) =>
    params.append('propertyIds', id),
  );
}

/**
 * Finance-Period-Selector: erlaubt "year" ODER "period" (oder beides — period gewinnt).
 * Default ist das aktuelle Jahr, damit Bestandscode ohne Argument weiter funktioniert.
 */
function buildPeriodParams(opts: { year?: number; period?: string }): URLSearchParams {
  const params = new URLSearchParams();
  if (opts.period) {
    params.set('period', opts.period);
  } else {
    params.set('year', String(opts.year ?? new Date().getUTCFullYear()));
  }
  return params;
}

export function buildApi(http: KyInstance) {
  return {
    plugins: buildPluginApi(http),
    automationControl: buildAutomationControlApi(http),
    pluginCases: buildPluginCasesApi(http),
    dynamicPricingOverview: {
      get: (propertyIds?: string[] | null) => {
        const searchParams = new URLSearchParams();
        appendPropertyIds(searchParams, propertyIds);
        return http
          .get('api/v1/dynamic-pricing/overview', { searchParams })
          .json<DynamicPricingOverviewResponse>();
      },
      updateTenantControl: (body: UpdateDynamicPricingTenantControlRequest) =>
        http
          .put('api/v1/dynamic-pricing/tenant-control', { json: body })
          .json<DynamicPricingTenantControlResponse>(),
    },

    dynamicPricingCalendar: {
      get: (
        propertyGroupId: string,
        from: string,
        days: 7 | 14 | 30 | 60,
        propertyIds?: string[] | null,
      ) => {
        const searchParams = new URLSearchParams({ from, days: String(days) });
        appendPropertyIds(searchParams, propertyIds);
        return http
          .get(`api/v1/property-groups/${propertyGroupId}/dynamic-pricing/calendar`, {
            searchParams,
          })
          .json<DynamicPricingCalendarResponse>();
      },
      getDetail: (propertyGroupId: string, propertyId: string, date: string) =>
        http
          .get(
            `api/v1/property-groups/${propertyGroupId}/dynamic-pricing/calendar/${propertyId}/${date}`,
          )
          .json<DynamicPricingCalendarDayDetailResponse>(),
    },

    dynamicPricingHistory: {
      get: (query: DynamicPricingHistoryQuery) => {
        const searchParams = new URLSearchParams();
        appendPropertyIds(searchParams, query.propertyIds);
        const arrays = {
          propertyGroupIds: query.propertyGroupIds,
          triggers: query.triggers,
          outcomes: query.outcomes,
          publicationStatuses: query.publicationStatuses,
          exposureKinds: query.exposureKinds,
        };
        Object.entries(arrays).forEach(([key, values]) =>
          values?.forEach((value) => searchParams.append(key, value)),
        );
        const values = {
          view: query.view,
          stayFrom: query.stayFrom,
          stayTo: query.stayTo,
          decisionFromUtc: query.decisionFromUtc,
          decisionToUtc: query.decisionToUtc,
          publicationCreatedFromUtc: query.publicationCreatedFromUtc,
          publicationCreatedToUtc: query.publicationCreatedToUtc,
          exposureConfirmedFromUtc: query.exposureConfirmedFromUtc,
          exposureConfirmedToUtc: query.exposureConfirmedToUtc,
          strategyProfileVersionId: query.strategyProfileVersionId,
          configurationVersionId: query.configurationVersionId,
          pageSize: query.pageSize,
          decisionCursor: query.decisionCursor,
          publicationCursor: query.publicationCursor,
          exposureCursor: query.exposureCursor,
        };
        Object.entries(values).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
        return http
          .get('api/v1/dynamic-pricing/history', { searchParams })
          .json<DynamicPricingHistoryResponse>();
      },
      /** ZIP mit dem Dateinamen des Servers (`Content-Disposition`), sofern er lesbar ist. */
      export: async (query: DynamicPricingHistoryQuery) => {
        // Der Server baut bis zu 120 s; der Standard von 30 s hielte das für „keine Verbindung“.
        const response = await http.post('api/v1/dynamic-pricing/history/export', {
          json: query,
          timeout: 130_000,
        });
        return {
          blob: await response.blob(),
          fileName: attachmentFileName(response.headers.get('Content-Disposition')),
        };
      },
    },

    pricingStrategyProfiles: {
      list: () =>
        http.get('api/v1/pricing-strategy-profiles').json<PricingStrategyProfileResponse[]>(),
      get: (versionId: string) =>
        http
          .get(`api/v1/pricing-strategy-profiles/${versionId}`)
          .json<PricingStrategyProfileResponse>(),
    },

    pricingConfigurations: {
      get: (propertyId: string) =>
        http
          .get(`api/v1/properties/${propertyId}/pricing-configuration`)
          .json<PricingConfigurationResponse>(),
      validate: (propertyId: string, configuration: PricingConfigurationInput) =>
        http
          .post(`api/v1/properties/${propertyId}/pricing-configuration/validate`, {
            json: configuration,
          })
          .json<PricingConfigurationValidationResponse>(),
      update: (propertyId: string, body: UpdatePricingConfigurationRequest) =>
        http
          .put(`api/v1/properties/${propertyId}/pricing-configuration`, { json: body })
          .json<PricingConfigurationResponse>(),
      preview: (propertyId: string, body: PricingConfigurationPreviewRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/pricing-configuration/preview`, { json: body })
          .json<PricingConfigurationPreviewResponse>(),
    },

    channelPriceModifiers: {
      get: (propertyId: string) =>
        http
          .get(`api/v1/properties/${propertyId}/channel-price-modifiers`)
          .json<ChannelPriceModifierItem[]>(),
      preview: (
        propertyId: string,
        connectionStateId: string,
        body: UpdateChannelPriceModifierRequest,
      ) =>
        http
          .post(
            `api/v1/properties/${propertyId}/channel-price-modifiers/${connectionStateId}/preview`,
            { json: body },
          )
          .json<ChannelPriceModifierPreview>(),
      update: (
        propertyId: string,
        connectionStateId: string,
        body: UpdateChannelPriceModifierRequest,
      ) =>
        http
          .put(`api/v1/properties/${propertyId}/channel-price-modifiers/${connectionStateId}`, {
            json: body,
          })
          .json<ChannelPriceModifierChangeAccepted>(),
    },

    search: {
      // Query ist PII → POST-Body, nie Query-String. signal aus dem TanStack-Query-Kontext
      // durchreichen, damit überholte Requests abgebrochen werden (Race-/Stale-Schutz).
      quick: (body: QuickSearchRequest, signal?: AbortSignal) =>
        http.post('api/v1/search/quick', { json: body, signal }).json<QuickSearchResponse>(),
      catalog: () => http.get('api/v1/search/catalog').json<SearchCatalogResponse>(),
      advanced: (body: AdvancedSearchRequest, signal?: AbortSignal) =>
        http.post('api/v1/search/advanced', { json: body, signal }).json<AdvancedSearchResponse>(),
      export: (body: AdvancedSearchRequest, format: SearchExportFormat) =>
        http.post('api/v1/search/export', { json: body, searchParams: { format } }).blob(),
      saved: {
        list: () => http.get('api/v1/search/saved').json<SavedSearchSummary[]>(),
        get: (id: string) => http.get(`api/v1/search/saved/${id}`).json<SavedSearchDetail>(),
        create: (body: SaveSearchRequest) =>
          http.post('api/v1/search/saved', { json: body }).json<SavedSearchDetail>(),
        update: (id: string, body: SaveSearchRequest) =>
          http.put(`api/v1/search/saved/${id}`, { json: body }).json<SavedSearchDetail>(),
        delete: (id: string) => http.delete(`api/v1/search/saved/${id}`).then(() => void 0),
        /**
         * Fuehrt die GESPEICHERTE Definition serverseitig aus — nicht das, was der Client aus dem
         * Detail rekonstruiert. Der Unterschied ist die SchemaVersion: der Server nimmt die
         * gespeicherte, der Client kennt nur seine eigene aktuelle. Rechte werden dabei immer neu
         * gegen den aktuellen Nutzer geprueft.
         */
        run: (id: string, body: RunSavedSearchRequest) =>
          http.post(`api/v1/search/saved/${id}/run`, { json: body }).json<AdvancedSearchResponse>(),
        export: (id: string, body: RunSavedSearchRequest, format: SearchExportFormat) =>
          http
            .post(`api/v1/search/saved/${id}/export`, { json: body, searchParams: { format } })
            .blob(),
      },
    },

    auth: {
      login: (body: LoginRequest) =>
        http.post('api/v1/auth/login', { json: body }).json<AuthLoginResponse>(),
      completeTwoFactor: (body: CompleteTwoFactorLoginRequest) =>
        http.post('api/v1/auth/two-factor/verify', { json: body }).json<Session>(),
      beginRequiredTwoFactorSetup: (challengeToken: string) =>
        http
          .post('api/v1/auth/two-factor/setup', { json: { challengeToken } })
          .json<TwoFactorSetupResponse>(),
      completeRequiredTwoFactorSetup: (challengeToken: string, code: string) =>
        http
          .post('api/v1/auth/two-factor/setup/confirm', { json: { challengeToken, code } })
          .json<RequiredTwoFactorSetupConfirmationResponse>(),
      completeRequiredTwoFactorSetupSession: (challengeToken: string) =>
        http
          .post('api/v1/auth/two-factor/setup/complete', { json: { challengeToken } })
          .json<Session>(),
      // Boot-Bootstrap: CSRF-Token aus dem Cookie holen (SPA hat ihn nach Neustart verloren).
      csrf: () => http.get('api/v1/auth/csrf').json<CsrfTokenResponse>(),
      // Kein Body (Refresh-Token = Cookie). Der X-CSRF-Token-Header wird NICHT hier gesetzt,
      // sondern vom HTTP-Client aus der Live-Session — sonst nutzt er einen veralteten Token,
      // falls beforeRequest zwischenzeitlich einen Refresh (mit CSRF-Rotation) auslöst.
      logout: () => http.post('api/v1/auth/logout').then(() => void 0),
      forgotPassword: (body: ForgotPasswordRequest) =>
        http.post('api/v1/auth/forgot-password', { json: body }).then(() => void 0),
      resetPassword: (body: ResetPasswordRequest) =>
        http.post('api/v1/auth/reset-password', { json: body }).then(() => void 0),
      activateAccount: (body: ActivateAccountRequest) =>
        http.post('api/v1/auth/activate-account', { json: body }).then(() => void 0),
    },

    accountSecurity: {
      getTwoFactor: () =>
        http.get('api/v1/account/security/two-factor').json<TwoFactorStatusResponse>(),
      beginTwoFactorSetup: (password: string) =>
        http
          .post('api/v1/account/security/two-factor/setup', { json: { password } })
          .json<TwoFactorSetupResponse>(),
      confirmTwoFactorSetup: (challengeToken: string, code: string) =>
        http
          .post('api/v1/account/security/two-factor/confirm', {
            json: { challengeToken, code },
          })
          .json<TwoFactorSetupCompletionResponse>(),
      regenerateRecoveryCodes: (body: ManageTwoFactorRequest) =>
        http
          .post('api/v1/account/security/two-factor/recovery-codes', { json: body })
          .json<TwoFactorSetupCompletionResponse>(),
      disableTwoFactor: (body: ManageTwoFactorRequest) =>
        http.post('api/v1/account/security/two-factor/disable', { json: body }).then(() => void 0),
    },

    users: {
      list: (q: UserListQuery = {}) => {
        const params = toQuery(q);
        if (q.role !== undefined) params.set('role', q.role);
        if (q.status !== undefined) params.set('status', q.status);
        return http.get('api/v1/users', { searchParams: params }).json<PagedResult<UserResponse>>();
      },
      getById: (id: string) => http.get(`api/v1/users/${id}`).json<UserResponse>(),
      getAccess: (id: string) => http.get(`api/v1/users/${id}/access`).json<UserAccessResponse>(),
      previewAccess: (id: string, body: UpdateUserAccessRequest) =>
        http
          .post(`api/v1/users/${id}/access/preview`, { json: body })
          .json<UserAccessPreviewResponse>(),
      create: (body: CreateUserRequest) =>
        http.post('api/v1/users', { json: body }).json<UserResponse>(),
      /** Nur Vor- und Nachname. Die E-Mail ist die Anmeldung und aendert sich hier nicht. */
      update: (id: string, body: UpdateUserRequest) =>
        http.put(`api/v1/users/${id}`, { json: body }).json<UserResponse>(),
      updateAccess: (id: string, body: UpdateUserAccessRequest) =>
        http.put(`api/v1/users/${id}/access`, { json: body }).json<UserResponse>(),
      deactivate: (id: string) => http.post(`api/v1/users/${id}/deactivate`).json<UserResponse>(),
      reactivate: (id: string) => http.post(`api/v1/users/${id}/reactivate`).json<UserResponse>(),
      resendInvitation: (id: string) =>
        http.post(`api/v1/users/${id}/resend-invitation`).then(() => void 0),
      resetTwoFactor: (id: string, body: ResetUserTwoFactorRequest) =>
        http.post(`api/v1/users/${id}/two-factor/reset`, { json: body }).then(() => void 0),
      /** Nur ein Plattform-Admin der Plattform, nie am eigenen Konto; Passwort und Code bestätigen. */
      grantPlatformAdmin: (id: string, body: PlatformAdminChangeRequest) =>
        http.post(`api/v1/users/${id}/platform-admin/grant`, { json: body }).then(() => void 0),
      revokePlatformAdmin: (id: string, body: PlatformAdminChangeRequest) =>
        http.post(`api/v1/users/${id}/platform-admin/revoke`, { json: body }).then(() => void 0),
    },

    /** Der eigene Workspace — immer der aus dem Token, nie über eine Id. */
    workspace: {
      get: () => http.get('api/v1/tenants/me').json<WorkspaceResponse>(),
      getSettings: () => http.get('api/v1/tenants/me/settings').json<WorkspaceSettingsResponse>(),
      updateSettings: (body: UpdateWorkspaceSettingsRequest) =>
        http.put('api/v1/tenants/me/settings', { json: body }).json<WorkspaceSettingsResponse>(),
    },

    workspaceTwoFactorPolicy: {
      get: () =>
        http.get('api/v1/tenants/me/two-factor-policy').json<WorkspaceTwoFactorPolicyResponse>(),
      update: (body: UpdateWorkspaceTwoFactorPolicyRequest) =>
        http
          .put('api/v1/tenants/me/two-factor-policy', { json: body })
          .json<WorkspaceTwoFactorPolicyResponse>(),
    },

    companies: {
      list: (q: QueryRequest = {}) =>
        http
          .get('api/v1/companies', { searchParams: toQuery(q) })
          .json<PagedResult<CompanyResponse>>(),
      getById: (id: string) => http.get(`api/v1/companies/${id}`).json<CompanyResponse>(),
      create: (body: CreateCompanyRequest) =>
        http.post('api/v1/companies', { json: body }).json<CompanyResponse>(),
      update: (id: string, body: UpdateCompanyRequest) =>
        http.put(`api/v1/companies/${id}`, { json: body }).json<CompanyResponse>(),
    },

    cleaningTeams: {
      list: () => http.get('api/v1/cleaning-teams').json<CleaningTeamResponse[]>(),
      getById: (id: string) => http.get(`api/v1/cleaning-teams/${id}`).json<CleaningTeamResponse>(),
      create: (body: CreateCleaningTeamRequest) =>
        http.post('api/v1/cleaning-teams', { json: body }).json<CleaningTeamResponse>(),
      update: (id: string, body: UpdateCleaningTeamRequest) =>
        http.put(`api/v1/cleaning-teams/${id}`, { json: body }).json<CleaningTeamResponse>(),
    },

    /**
     * Plattformverwaltung der Workspaces. Serverseitig PlatformAdmin-only; das Frontend blendet
     * die Flaeche entsprechend aus, der Server bleibt finale Instanz.
     */
    workspaces: {
      list: (q: QueryRequest = {}) =>
        http
          .get('api/v1/admin/workspaces', { searchParams: toQuery(q) })
          .json<PagedResult<WorkspaceSummaryResponse>>(),
      create: (body: CreateWorkspaceRequest) =>
        http.post('api/v1/admin/workspaces', { json: body }).json<WorkspaceSummaryResponse>(),
      activate: (id: string) =>
        http.post(`api/v1/admin/workspaces/${id}/activate`).json<WorkspaceSummaryResponse>(),
      deactivate: (id: string) =>
        http.post(`api/v1/admin/workspaces/${id}/deactivate`).json<WorkspaceSummaryResponse>(),
      resendAdminInvitation: (workspaceId: string, userId: string) =>
        http
          .post(`api/v1/admin/workspaces/${workspaceId}/admins/${userId}/invitation`)
          .then(() => void 0),
    },

    supportSessions: buildSupportSessionApi(http),

    propertyAssignments: {
      list: (filter: { propertyId?: string; userId?: string; companyId?: string } = {}) => {
        const params = new URLSearchParams();
        if (filter.propertyId) params.set('propertyId', filter.propertyId);
        if (filter.userId) params.set('userId', filter.userId);
        if (filter.companyId) params.set('companyId', filter.companyId);
        return http
          .get('api/v1/property-assignments', { searchParams: params })
          .json<PropertyAssignmentResponse[]>();
      },
      assign: (body: CreatePropertyAssignmentRequest) =>
        http.post('api/v1/property-assignments', { json: body }).json<PropertyAssignmentResponse>(),
      revoke: (id: string) => http.delete(`api/v1/property-assignments/${id}`).then(() => void 0),
    },

    properties: {
      list: (q: PropertyListQuery = {}) => {
        const params = toQuery(q);
        if (q.lifecycle !== undefined) params.set('lifecycle', q.lifecycle);
        return http
          .get('api/v1/properties', { searchParams: params })
          .json<PagedResult<PropertyResponse>>();
      },
      get: (id: string) => http.get(`api/v1/properties/${id}`).json<PropertyResponse>(),
      create: (body: CreatePropertyRequest) =>
        http.post('api/v1/properties', { json: body }).json<PropertyResponse>(),
      update: (id: string, body: UpdatePropertyRequest) =>
        http.put(`api/v1/properties/${id}`, { json: body }).json<PropertyResponse>(),
      archivePreflight: (id: string) =>
        http
          .get(`api/v1/properties/${id}/archive-preflight`)
          .json<PropertyArchivePreflightResponse>(),
      archive: (id: string, reason?: string) =>
        http
          .post(`api/v1/properties/${id}/archive`, { json: { reason: reason || null } })
          .json<PropertyArchiveResponse>(),
      /**
       * Freigabe ohne Kanalmanager (Draft → Active). Nur fuer Apartments, die bei Channex weder
       * angelegt sind noch gerade angelegt werden — sonst 409 und die Veroeffentlichung ist der Weg.
       */
      releaseWithoutChannelManager: (id: string, reason?: string) =>
        http
          .post(`api/v1/properties/${id}/release-without-channel-manager`, {
            json: { reason: reason || null },
          })
          .json<PropertyResponse>(),
      /**
       * Nimmt ein Apartment voruebergehend aus dem Vertrieb bzw. zurueck hinein. Beides wirkt auf
       * die Verteilung: der Statuswechsel reiht den Schliess- bzw. Oeffnungs-Push mit ein.
       */
      pause: (id: string) => http.post(`api/v1/properties/${id}/pause`).json<PropertyResponse>(),
      activate: (id: string) =>
        http.post(`api/v1/properties/${id}/activate`).json<PropertyResponse>(),
      /** Setzt das Cover-Bild auf eine PropertyFile-ID (oder null zum Loeschen). */
      setCover: (id: string, coverFileId: string | null) =>
        http
          .put(`api/v1/properties/${id}/cover`, { json: { coverFileId } })
          .json<PropertyResponse>(),
      // Sprint 11 Issue 2: Stay-Info (WLAN, Hausordnung, Notruf) — separater
      // Endpoint damit WiFiPassword im Audit-Diff redacted bleibt.
      updateStayInfo: (id: string, body: UpdatePropertyStayInfoRequest) =>
        http.put(`api/v1/properties/${id}/stay-info`, { json: body }).json<PropertyResponse>(),
      updateCleaningCapacity: (id: string, body: UpdatePropertyCleaningCapacityRequest) =>
        http
          .put(`api/v1/properties/${id}/cleaning-capacity`, { json: body })
          .json<PropertyResponse>(),
      /** Wohnungsverbund aus Sicht des Zimmers (Vorschau, Verknüpfen, Lösen). */
      wholeFlat: buildWholeFlatApi(http),
      accessMapping: {
        get: (id: string) =>
          http.get(`api/v1/properties/${id}/access-mapping`).json<PropertyAccessMappingResponse>(),
        setStaticCode: (id: string, body: SetStaticAccessCodeRequest) =>
          http
            .put(`api/v1/properties/${id}/access-mapping/static-code`, { json: body })
            .json<PropertyAccessMappingResponse>(),
        remove: (id: string) =>
          http.delete(`api/v1/properties/${id}/access-mapping`).then(() => void 0),
      },
    },

    addressVerification: {
      search: (body: AddressSearchRequest, signal?: AbortSignal) =>
        http
          .post('api/v1/address-verification/search', { json: body, signal })
          .json<AddressSuggestionResponse[]>(),
    },

    propertyGroups: {
      list: () => http.get('api/v1/property-groups').json<PropertyGroupResponse[]>(),
      get: (id: string) => http.get(`api/v1/property-groups/${id}`).json<PropertyGroupResponse>(),
      update: (id: string, body: UpsertPropertyGroupRequest) =>
        http.put(`api/v1/property-groups/${id}`, { json: body }).json<PropertyGroupResponse>(),
      moveProperty: (propertyId: string, targetGroupId: string) =>
        http
          .post(`api/v1/property-groups/properties/${propertyId}/move`, {
            json: { targetGroupId },
          })
          .json<PropertyGroupResponse>(),
      /** Setzt den Kartenpin von Hand; danach bleibt die automatische Ermittlung aussen vor. */
      setCoordinates: (id: string, latitude: number, longitude: number) =>
        http
          .put(`api/v1/property-groups/${id}/coordinates`, { json: { latitude, longitude } })
          .json<PropertyGroupResponse>(),
      /** Schiebt alle Apartments von `id` nach `targetGroupId`; `id` verschwindet dabei. */
      merge: (id: string, body: MergePropertyGroupsRequest) =>
        http
          .post(`api/v1/property-groups/${id}/merge`, { json: body })
          .json<PropertyGroupResponse>(),
      split: (id: string, body: SplitPropertyGroupRequest) =>
        http
          .post(`api/v1/property-groups/${id}/split`, { json: body })
          .json<PropertyGroupResponse>(),
      /** Nur für leere Objekte — der Server lehnt ein Objekt mit Apartments ab. */
      archive: (id: string) => http.delete(`api/v1/property-groups/${id}`).then(() => void 0),
      guestExperience: {
        get: (id: string) =>
          http
            .get(`api/v1/property-groups/${id}/guest-experience`)
            .json<GuestExperienceSettingsResponse>(),
        update: (id: string, body: UpdateGuestExperienceSettingsRequest) =>
          http
            .put(`api/v1/property-groups/${id}/guest-experience`, { json: body })
            .json<GuestExperienceSettingsResponse>(),
        preview: (id: string, body: GuestExperiencePreviewRequest) =>
          http
            .post(`api/v1/property-groups/${id}/guest-experience/preview`, { json: body })
            .json<GuestExperiencePreviewResponse>(),
        messages: {
          get: (id: string) =>
            http
              .get(`api/v1/property-groups/${id}/guest-experience/messages`)
              .json<PropertyCommunicationCatalogResponse>(),
          update: (id: string, body: UpdatePropertyCommunicationCatalogRequest) =>
            http
              .put(`api/v1/property-groups/${id}/guest-experience/messages`, { json: body })
              .json<PropertyCommunicationCatalogResponse>(),
          preview: (id: string, body: PropertyCommunicationPreviewRequest) =>
            http
              .post(`api/v1/property-groups/${id}/guest-experience/messages/preview`, {
                json: body,
              })
              .json<PropertyCommunicationPreviewResponse>(),
        },
        branding: {
          get: (id: string) =>
            http
              .get(`api/v1/property-groups/${id}/guest-experience/branding`)
              .json<PropertyPortalBrandingResponse>(),
          update: (id: string, body: UpdatePropertyPortalBrandingRequest) =>
            http
              .put(`api/v1/property-groups/${id}/guest-experience/branding`, { json: body })
              .json<PropertyPortalBrandingResponse>(),
        },
        uploadLogo: (id: string, file: File, expectedConcurrency?: number | null) => {
          const form = new FormData();
          form.append('file', file);
          if (expectedConcurrency != null) {
            form.append('expectedConcurrency', String(expectedConcurrency));
          }
          return http
            .post(`api/v1/property-groups/${id}/guest-experience/logo`, { body: form })
            .json<PropertyPortalBrandingResponse>();
        },
        logoUrl: (id: string, version: number) =>
          `api/v1/property-groups/${id}/guest-experience/logo?v=${version}`,
        removeLogo: (id: string, expectedConcurrency?: number | null) =>
          http
            .delete(`api/v1/property-groups/${id}/guest-experience/logo`, {
              searchParams:
                expectedConcurrency == null
                  ? undefined
                  : { expectedConcurrency: String(expectedConcurrency) },
            })
            .json<PropertyPortalBrandingResponse>(),
      },
    },

    operations: {
      day: (propertyGroupId: string, date: string) =>
        http
          .get(`api/v1/admin/operations/property-groups/${propertyGroupId}/day`, {
            searchParams: { date },
          })
          .json<OperationsDayResponse>(),
    },

    /**
     * Anfahrhilfe. Am Objekt entsteht die gemeinsame Schrittfolge, am Apartment kommen eigene
     * Schritte dazu oder einzelne geerbte werden überschrieben.
     */
    arrivalSteps: {
      listForObject: (groupId: string, kind: ArrivalStepKind = 'Arrival') =>
        http
          .get(`api/v1/property-groups/${groupId}/arrival-steps`, { searchParams: { kind } })
          .json<ArrivalStepListResponse>(),
      createForObject: (
        groupId: string,
        body: UpsertArrivalStepRequest,
        kind: ArrivalStepKind = 'Arrival',
      ) =>
        http
          .post(`api/v1/property-groups/${groupId}/arrival-steps`, {
            json: body,
            searchParams: { kind },
          })
          .json<ArrivalStepResponse>(),
      updateForObject: (
        groupId: string,
        stepId: string,
        body: UpsertArrivalStepRequest,
        kind: ArrivalStepKind = 'Arrival',
      ) =>
        http
          .put(`api/v1/property-groups/${groupId}/arrival-steps/${stepId}`, {
            json: body,
            searchParams: { kind },
          })
          .json<ArrivalStepResponse>(),
      deleteForObject: (groupId: string, stepId: string, kind: ArrivalStepKind = 'Arrival') =>
        http
          .delete(`api/v1/property-groups/${groupId}/arrival-steps/${stepId}`, {
            searchParams: { kind },
          })
          .json<DeleteArrivalStepResponse>(),
      previewForObject: (groupId: string, body: ArrivalStepPreviewRequest) =>
        http
          .post(`api/v1/property-groups/${groupId}/arrival-steps/preview`, { json: body })
          .json<ArrivalStepPreviewResponse>(),
      moveForObject: (
        groupId: string,
        stepId: string,
        direction: 'Up' | 'Down',
        kind: ArrivalStepKind = 'Arrival',
      ) =>
        http
          .post(`api/v1/property-groups/${groupId}/arrival-steps/${stepId}/move`, {
            json: { direction },
            searchParams: { kind },
          })
          .json<ArrivalStepListResponse>(),

      listForApartment: (propertyId: string, kind: ArrivalStepKind = 'Arrival') =>
        http
          .get(`api/v1/properties/${propertyId}/arrival-steps`, { searchParams: { kind } })
          .json<ArrivalStepListResponse>(),
      createForApartment: (
        propertyId: string,
        body: UpsertArrivalStepRequest,
        kind: ArrivalStepKind = 'Arrival',
      ) =>
        http
          .post(`api/v1/properties/${propertyId}/arrival-steps`, {
            json: body,
            searchParams: { kind },
          })
          .json<ArrivalStepResponse>(),
      /** Eigener Schritt ändern — oder aus einem geerbten die Überschreibung machen. */
      updateForApartment: (
        propertyId: string,
        stepId: string,
        body: UpsertArrivalStepRequest,
        kind: ArrivalStepKind = 'Arrival',
      ) =>
        http
          .put(`api/v1/properties/${propertyId}/arrival-steps/${stepId}`, {
            json: body,
            searchParams: { kind },
          })
          .json<ArrivalStepResponse>(),
      deleteForApartment: (propertyId: string, stepId: string, kind: ArrivalStepKind = 'Arrival') =>
        http
          .delete(`api/v1/properties/${propertyId}/arrival-steps/${stepId}`, {
            searchParams: { kind },
          })
          .then(() => void 0),
      resetOverride: (propertyId: string, stepId: string, kind: ArrivalStepKind = 'Arrival') =>
        http
          .delete(`api/v1/properties/${propertyId}/arrival-steps/${stepId}/override`, {
            searchParams: { kind },
          })
          .json<ArrivalStepResponse>(),
      /**
       * Bilder hängen am Schritt, nicht an der Ebene: welcher Riegel gilt, leitet der Server
       * aus dem Schritt ab. Deshalb trägt die Route nur die Schritt-Id.
       */
      images: {
        list: (stepId: string) =>
          http.get(`api/v1/arrival-steps/${stepId}/images`).json<ArrivalImageListResponse>(),
        upload: (stepId: string, file: File) => {
          const body = new FormData();
          body.append('file', file);
          return http
            .post(`api/v1/arrival-steps/${stepId}/images`, { body })
            .json<ArrivalImageResponse>();
        },
        remove: (stepId: string, imageId: string) =>
          http.delete(`api/v1/arrival-steps/${stepId}/images/${imageId}`).then(() => void 0),
        move: (stepId: string, imageId: string, direction: 'Up' | 'Down') =>
          http
            .post(`api/v1/arrival-steps/${stepId}/images/${imageId}/move`, {
              json: { direction },
            })
            .json<ArrivalImageListResponse>(),
        setAnnotations: (stepId: string, imageId: string, annotations: ArrivalAnnotation[]) =>
          http
            .put(`api/v1/arrival-steps/${stepId}/images/${imageId}/annotations`, {
              json: { annotations },
            })
            .json<ArrivalImageResponse>(),
        content: (stepId: string, imageId: string, variant: 'thumb' | 'full') =>
          http
            .get(`api/v1/arrival-steps/${stepId}/images/${imageId}/content`, {
              searchParams: { variant },
            })
            .blob(),
      },
      previewForApartment: (propertyId: string, body: ArrivalStepPreviewRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/arrival-steps/preview`, { json: body })
          .json<ArrivalStepPreviewResponse>(),
      moveForApartment: (
        propertyId: string,
        stepId: string,
        direction: 'Up' | 'Down',
        kind: ArrivalStepKind = 'Arrival',
      ) =>
        http
          .post(`api/v1/properties/${propertyId}/arrival-steps/${stepId}/move`, {
            json: { direction },
            searchParams: { kind },
          })
          .json<ArrivalStepListResponse>(),
    },

    bookings: {
      list: (q: BookingListQuery = {}) => {
        const params = new URLSearchParams();
        if (q.page !== undefined) params.set('page', String(q.page));
        if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
        if (q.sort) params.set('sort', q.sort);
        if (q.status) params.set('status', q.status);
        if (q.dateFrom) params.set('dateFrom', q.dateFrom);
        if (q.dateTo) params.set('dateTo', q.dateTo);
        if (q.checkOutFrom) params.set('checkOutFrom', q.checkOutFrom);
        if (q.propertyId) params.set('propertyId', q.propertyId);
        if (q.search) params.set('search', q.search);
        appendPropertyIds(params, q.propertyIds);
        return http.get('api/v1/bookings', { searchParams: params }).json<BookingListResponse>();
      },
      get: (id: string) => http.get(`api/v1/bookings/${id}`).json<BookingResponse>(),
      /**
       * Manuelle Buchung (Walk-in/Telefon/Direkt). Backend schreibt Booking
       * direkt (Source=Manuell) und triggert asynchron einen StopSell-Push an
       * Channex via AriPushOutbox. 404 → Property nicht im Tenant, 409 → Range
       * kollidiert mit aktiver Buchung.
       */
      createManual: (body: CreateManualBookingRequest) =>
        http.post('api/v1/bookings', { json: body }).json<BookingResponse>(),
      /**
       * Aendert Datum + Gaeste einer Manual-Buchung (A4). Preis bleibt server-abgeleitet.
       * 404 → nicht im Tenant, 409 → OTA-Buchung / NoShow / Range-Kollision.
       */
      update: (id: string, body: UpdateBookingRequest) =>
        http.put(`api/v1/bookings/${id}`, { json: body }).json<BookingResponse>(),
      // `POST bookings/{id}/cancel` hat keinen Client mehr: der Weg fuehrt ueber
      // `cancellationPreview` + `createCancellation`, die auch bezahlte Buchungen abdecken.
      cancellationPreview: (id: string) =>
        http
          .get(`api/v1/bookings/${id}/cancellation-preview`)
          .json<BookingCancellationPreviewResponse>(),
      // Nach der Erstattung wartet der Server bis zu FlatFamilyLocks:PatientWaitMilliseconds (60 s)
      // auf das Apartment, statt aufzugeben; der Standard-Timeout von 30 s hiesse sonst „Fehler",
      // während er noch storniert.
      createCancellation: (id: string, body: CreateBookingCancellationRequest) =>
        http
          .post(`api/v1/bookings/${id}/cancellations`, { json: body, timeout: 90_000 })
          .json<BookingCancellationResponse>(),
      reassignmentPreview: (id: string, targetPropertyId: string) =>
        http
          .get(`api/v1/bookings/${id}/reassignment-preview`, {
            searchParams: { targetPropertyId },
          })
          .json<BookingReassignmentPreviewResponse>(),
      createReassignment: (id: string, body: CreateBookingReassignmentRequest) =>
        http
          .post(`api/v1/bookings/${id}/reassignments`, { json: body })
          .json<BookingReassignmentResponse>(),
      reassignmentHistory: (id: string) =>
        http.get(`api/v1/bookings/${id}/reassignments`).json<BookingReassignmentResponse[]>(),
      upgradeCharges: (id: string) =>
        http.get(`api/v1/bookings/${id}/upgrade-charges`).json<UpgradeChargeResponse[]>(),
      createUpgradeCharge: (id: string, body: CreateUpgradeChargeRequest) =>
        http
          .post(`api/v1/bookings/${id}/upgrade-charges`, { json: body })
          .json<UpgradeChargeResponse>(),
      sendUpgradeCharge: (id: string, chargeId: string) =>
        http
          .post(`api/v1/bookings/${id}/upgrade-charges/${chargeId}/send`)
          .json<UpgradeChargeResponse>(),
      cancelUpgradeCharge: (id: string, chargeId: string) =>
        http
          .post(`api/v1/bookings/${id}/upgrade-charges/${chargeId}/cancel`)
          .json<UpgradeChargeResponse>(),
      recordEarlyDeparture: (id: string, body: CreateOtaEarlyDepartureRequest) =>
        http
          .post(`api/v1/bookings/${id}/early-departure`, { json: body })
          .json<OtaEarlyDepartureResponse>(),
      upsertEarlyDeparturePlan: (id: string, body: UpsertEarlyDeparturePlanRequest) =>
        http
          .put(`api/v1/bookings/${id}/early-departure-plan`, { json: body })
          .json<EarlyDeparturePlanSummary>(),
      cancelEarlyDeparturePlan: (id: string, body: CancelEarlyDeparturePlanRequest) =>
        http
          .post(`api/v1/bookings/${id}/early-departure-plan/cancel`, { json: body })
          .json<EarlyDeparturePlanSummary>(),
    },

    calendar: {
      getRange: (from: string, to: string, propertyIds?: string[] | null) => {
        const params = new URLSearchParams({ from, to });
        appendPropertyIds(params, propertyIds);
        return http.get('api/v1/calendar', { searchParams: params }).json<CalendarResponse>();
      },
      getOnboardingSupport: (bookingId: string, propertyId: string) =>
        http
          .get(`api/v1/admin/bookings/${bookingId}/onboarding-support`, {
            searchParams: { propertyId },
          })
          .json<OnboardingSupportResponse>(),
      openOnboardingGuestLink: (bookingId: string, propertyId: string) =>
        http
          .post(`api/v1/admin/bookings/${bookingId}/onboarding-support/guest-link`, {
            searchParams: { propertyId },
          })
          .json<{ url: string }>(),
    },

    availabilityDistribution: {
      calendar: (propertyGroupId: string, from: string, to: string) =>
        http
          .get(`api/v1/property-groups/${propertyGroupId}/availability-distribution/calendar`, {
            searchParams: { from, to },
          })
          .json<AvailabilityDistributionCalendarResponse>(),
      closeouts: {
        list: (
          propertyGroupId: string,
          page: number,
          status = 'active',
          window?: { from: string; to: string },
        ) =>
          http
            .get(`api/v1/property-groups/${propertyGroupId}/channel-closeouts`, {
              searchParams: { page, pageSize: window ? 100 : 25, status, ...window },
            })
            .json<ChannelCloseoutPage>(),
        get: (propertyGroupId: string, closeoutId: string) =>
          http
            .get(`api/v1/property-groups/${propertyGroupId}/channel-closeouts/${closeoutId}`)
            .json<ChannelCloseoutDetail>(),
        options: (propertyGroupId: string) =>
          http
            .get(`api/v1/property-groups/${propertyGroupId}/channel-closeouts/options`)
            .json<ChannelCloseoutOptions>(),
        preview: (propertyGroupId: string, body: ChannelCloseoutWriteRequest) =>
          http
            .post(`api/v1/property-groups/${propertyGroupId}/channel-closeouts/preview`, {
              json: body,
            })
            .json<ChannelCloseoutPreview>(),
        create: (propertyGroupId: string, body: ChannelCloseoutWriteRequest) =>
          http
            .post(`api/v1/property-groups/${propertyGroupId}/channel-closeouts`, { json: body })
            .json<ChannelCloseoutAccepted>(),
        update: (propertyGroupId: string, closeoutId: string, body: ChannelCloseoutUpdateRequest) =>
          http
            .put(`api/v1/property-groups/${propertyGroupId}/channel-closeouts/${closeoutId}`, {
              json: body,
            })
            .json<ChannelCloseoutAccepted>(),
        remove: (propertyGroupId: string, closeoutId: string, expectedRevision: number) =>
          http
            .delete(`api/v1/property-groups/${propertyGroupId}/channel-closeouts/${closeoutId}`, {
              searchParams: { expectedRevision },
            })
            .json<ChannelCloseoutAccepted>(),
        retry: (propertyGroupId: string, closeoutId: string, body: ChannelCloseoutActionRequest) =>
          http
            .post(
              `api/v1/property-groups/${propertyGroupId}/channel-closeouts/${closeoutId}/retry`,
              {
                json: body,
              },
            )
            .json<ChannelCloseoutAccepted>(),
        reconcile: (
          propertyGroupId: string,
          closeoutId: string,
          body: ChannelCloseoutActionRequest,
        ) =>
          http
            .post(
              `api/v1/property-groups/${propertyGroupId}/channel-closeouts/${closeoutId}/reconcile`,
              {
                json: body,
              },
            )
            .json<ChannelCloseoutAccepted>(),
        confirmAbsentAndReissue: (
          propertyGroupId: string,
          closeoutId: string,
          body: ChannelCloseoutActionRequest,
        ) =>
          http
            .post(
              `api/v1/property-groups/${propertyGroupId}/channel-closeouts/${closeoutId}/confirm-absent-and-reissue`,
              { json: body },
            )
            .json<ChannelCloseoutAccepted>(),
      },
    },

    channex: {
      listWebhookEvents: (q: QueryRequest & { status?: string } = {}) => {
        const params = toQuery(q);
        if (q.status) params.set('status', q.status);
        return http
          .get('api/v1/admin/channex/webhook-events', { searchParams: params })
          .json<PagedResult<ChannexWebhookEventResponse>>();
      },
      provisionProperty: (propertyId: string) =>
        http
          .post(`api/v1/admin/channex/properties/${propertyId}/provision`)
          .json<ChannexProvisioningResult>(),
      publicationState: (propertyGroupId: string) =>
        http
          .get(`api/v1/admin/channex/property-groups/${propertyGroupId}/publication`)
          .json<ChannexPublicationResponse>(),
      publishObjectParent: (propertyGroupId: string) =>
        http
          .post(`api/v1/admin/channex/property-groups/${propertyGroupId}/publish-parent`)
          .json<unknown>(),
      publishObjectUnits: (propertyGroupId: string, propertyIds: string[]) =>
        http
          .post(`api/v1/admin/channex/property-groups/${propertyGroupId}/publish-units`, {
            json: { propertyIds },
          })
          .json<unknown>(),
      /**
       * Manueller Abgleich einer mehrdeutigen Bindung — PlatformAdmin-only, weil er eine
       * Vendor-Identität festschreibt. Kein Wiederholen: entweder die Kennung übernehmen oder
       * bestätigen, dass beim Vendor nichts existiert (§6.102).
       */
      reconcileParentBinding: (
        bindingId: string,
        body: { action: ChannexReconcileAction; remoteId?: string },
      ) =>
        http
          .post(`api/v1/admin/channex/parent-bindings/${bindingId}/reconcile`, { json: body })
          .json<unknown>(),
      reconcileUnitBinding: (
        bindingId: string,
        body: {
          action: ChannexReconcileAction;
          resource: ChannexUnitResource;
          remoteId?: string;
        },
      ) =>
        http
          .post(`api/v1/admin/channex/unit-bindings/${bindingId}/reconcile`, { json: body })
          .json<unknown>(),
      listChannels: () =>
        http.get('api/v1/admin/channex/channels').json<{ data: ChannexChannelResource[] }>(),
      lastBookingsPerOta: (propertyId: string) =>
        http
          .get(`api/v1/admin/channex/properties/${propertyId}/last-bookings-per-ota`)
          .json<LastBookingsPerOtaResponse>(),
      syncActivity: (propertyId: string) =>
        http
          .get(`api/v1/admin/channex/properties/${propertyId}/sync-activity`)
          .json<SyncActivityResponse>(),
      syncActivityDetails: (kind: 'webhook' | 'audit', referenceId: string) =>
        http
          .get(`api/v1/admin/channex/sync-activity/${kind}/${referenceId}`)
          .json<SyncActivityDetails>(),
      ariPushHistory: (propertyId: string, limit = 20) =>
        http
          .get(`api/v1/admin/channex/properties/${propertyId}/ari-push-history`, {
            searchParams: { limit },
          })
          .json<AriPushHistoryResponse>(),
      ariState: (propertyId: string, from: string, to: string) =>
        http
          .get(`api/v1/admin/channex/properties/${propertyId}/ari-state`, {
            searchParams: { from, to },
          })
          .json<AriStateResponse>(),
      createChannel: (body: {
        title: string;
        channel: string;
        currency: string;
        hotelId: string;
        channexPropertyId: string;
      }) =>
        http
          .post('api/v1/admin/channex/channels', { json: body })
          .json<{ data: ChannexChannelResource }>(),
      deleteChannel: (channelId: string) =>
        http.delete(`api/v1/admin/channex/channels/${channelId}`).then(() => void 0),
      createIframeSession: (channexPropertyId: string, view: 'channels' | 'mapping' = 'channels') =>
        http
          .post('api/v1/admin/channex/iframe-session', {
            json: { channexPropertyId, view },
          })
          .json<ChannexIframeSession>(),
      listConnections: () =>
        http.get('api/v1/admin/channex/connections').json<ChannexConnectionsResponse>(),
      /**
       * Angenommene, aber nicht angewendete Revisionen des eigenen Workspace — Quarantaene vor
       * Wartenden, mit den Gesamtzahlen, ohne Payload und ohne Gastnamen.
       */
      quarantinedRevisions: (limit = 25) =>
        http
          .get('api/v1/admin/channex/quarantined-revisions', { searchParams: { limit } })
          .json<ChannexRevisionHoldList>(),
      /** „Erneut verarbeiten" aus dem gespeicherten Stand; die Antwort sagt, was geschah. */
      replayQuarantinedRevision: (id: string) =>
        http
          .post(`api/v1/admin/channex/quarantined-revisions/${id}/replay`)
          .json<ChannexRevisionReplayResult>(),
      /**
       * Gibt die Naechte dieser Revision beim Kanalmanager wieder frei, indem unsere Verfuegbarkeit
       * neu abgeleitet und gepusht wird. Ausdrueckliche Aktion nur der Workspace-Verwaltung: bei
       * einer echten Buchung in Quarantaene waere das eine Ueberbuchung.
       */
      rederiveQuarantinedAvailability: (id: string) =>
        http
          .post(`api/v1/admin/channex/quarantined-revisions/${id}/rederive-availability`)
          .json<ChannexQuarantineRederiveResult>(),
      createConnectUrl: (channelCode: string) =>
        http
          .post(`api/v1/admin/channex/connections/${channelCode}/connect-url`)
          .json<ChannexIframeSession>(),
      pushAri: (body: AriPushRequest) =>
        http.post('api/v1/admin/channex/ari', { json: body }).json<AriPushQueuedResult>(),
      /**
       * Multi-Range-Push — sendet bis zu 50 Date-Ranges in einem Channex-Call.
       * UI: Bulk-Edit-Modal mit Tabelle.
       */
      pushAriBatch: (body: AriPushBatchRequest) =>
        http
          .post('api/v1/admin/channex/ari/batch', { json: body })
          .json<AriBatchPushQueuedResult>(),
      releaseAriOwnership: (body: AriOwnershipReleaseRequest) =>
        http
          .post('api/v1/admin/channex/ari/ownership/release', { json: body })
          .json<AriOwnershipReleaseResult>(),
      ariOwnershipImpact: (propertyId: string, from: string, to: string) =>
        http
          .get('api/v1/admin/channex/ari/ownership/impact', {
            searchParams: { propertyId, from, to },
          })
          .json<AriOwnershipImpact>(),
      ariOperationStatus: (propertyId: string, operationIds: string[]) =>
        http
          .get('api/v1/admin/channex/ari/operations', {
            searchParams: new URLSearchParams([
              ['propertyId', propertyId],
              ...operationIds.map((id): [string, string] => ['operationIds', id]),
            ]),
          })
          .json<AriOperationStatusResponse>(),
      // Full Sync: pusht den echten Kalender-Zustand (Rates aus PropertyAriDay, Availability
      // aus Buchungen) als EINE Batch-Zeile → genau 2 Calls. 202 Accepted.
      fullSync: (propertyId: string) =>
        http
          .post('api/v1/admin/channex/ari/full-sync', { json: { propertyId } })
          .json<AriBatchPushQueuedResult>(),
      fullSyncPreview: (propertyId: string) =>
        http
          .get('api/v1/admin/channex/ari/full-sync/preview', { searchParams: { propertyId } })
          .json<FullSyncPreview>(),
      checkAvailability: (propertyId: string, from: string, to: string) =>
        http
          .get('api/v1/admin/channex/bookings/availability', {
            searchParams: { propertyId, from, to },
          })
          .json<AvailabilityResult>(),
      deadLetterList: () =>
        http.get('api/v1/admin/channex/outbox/dead-letter').json<OutboxDeadLetterItem[]>(),
      resendDeadLetter: (source: 'single' | 'batch', id: string) =>
        http
          .post('api/v1/admin/channex/outbox/dead-letter/resend', { json: { source, id } })
          .json<{ resent: boolean }>(),
    },

    cleaning: {
      scopes: () => http.get('api/v1/cleaning-tasks/scopes').json<CleaningScopeResponse[]>(),
      list: (
        filter: {
          status?: CleaningTaskStatus;
          scheduledFor?: string;
          propertyId?: string;
          propertyIds?: string[] | null;
          assignedToUserId?: string;
        } = {},
      ) => {
        const params = new URLSearchParams();
        if (filter.status) params.set('status', filter.status);
        if (filter.scheduledFor) params.set('scheduledFor', filter.scheduledFor);
        if (filter.propertyId) params.set('propertyId', filter.propertyId);
        if (filter.assignedToUserId) params.set('assignedToUserId', filter.assignedToUserId);
        appendPropertyIds(params, filter.propertyIds);
        return http
          .get('api/v1/cleaning-tasks', { searchParams: params })
          .json<CleaningTaskQueueResponse[]>();
      },
      summary: (filter: {
        from: string;
        to: string;
        propertyIds?: string[] | null;
        assignedToUserId?: string;
      }) => {
        const params = new URLSearchParams({ from: filter.from, to: filter.to });
        if (filter.assignedToUserId) params.set('assignedToUserId', filter.assignedToUserId);
        appendPropertyIds(params, filter.propertyIds);
        return http
          .get('api/v1/cleaning-tasks/summary', { searchParams: params })
          .json<CleaningRangeSummaryResponse[]>();
      },
      completionReport: (filter: CleaningCompletionReportFilter) => {
        const params = new URLSearchParams({ from: filter.from, to: filter.to });
        if (filter.assignedToUserId) params.set('assignedToUserId', filter.assignedToUserId);
        if (filter.selectedPropertyId) params.set('selectedPropertyId', filter.selectedPropertyId);
        appendPropertyIds(params, filter.propertyIds);
        return http
          .get('api/v1/cleaning-completion-reports', { searchParams: params })
          .json<CleaningCompletionReportResponse>();
      },
      getDetail: (id: string) =>
        http.get(`api/v1/cleaning-tasks/${id}`).json<CleaningTaskDetailResponse>(),
      get: async (id: string): Promise<CleaningTaskResponse> => {
        const detail = await http
          .get(`api/v1/cleaning-tasks/${id}`)
          .json<CleaningTaskDetailResponse>();
        const task = detail.summary;
        return {
          id: task.id,
          propertyId: task.propertyId,
          bookingId: task.bookingId,
          scheduledFor: task.scheduledFor,
          scheduledFrom: `${task.scheduledFromLocalDate}T${task.scheduledFromLocalTime}`,
          scheduledTo:
            task.scheduledToLocalDate && task.scheduledToLocalTime
              ? `${task.scheduledToLocalDate}T${task.scheduledToLocalTime}`
              : null,
          status: task.status,
          assignedToUserId: task.assignedToUserId,
          assignedToUserName: task.assignedToUserName,
          startedAt: task.startedAt,
          completedAt: task.completedAt,
          notes: detail.notes,
          completionNotes: detail.completionNotes,
          items: detail.items,
          photoSlotCount: task.photoSlotCount,
          filledPhotoSlotCount: task.filledPhotoSlotCount,
          photos: detail.photos,
          damages: detail.damages,
        };
      },
      create: (body: CreateCleaningTaskRequest) =>
        http.post('api/v1/cleaning-tasks', { json: body }).json<CleaningTaskResponse>(),
      assignableUsers: (id: string) =>
        http.get(`api/v1/cleaning-tasks/${id}/assignable-users`).json<CleaningAssigneeResponse[]>(),
      assign: (id: string, body: AssignCleaningTaskRequest) =>
        http
          .post(`api/v1/cleaning-tasks/${id}/assign`, { json: body })
          .json<CleaningTaskResponse>(),
      start: (id: string) =>
        http.post(`api/v1/cleaning-tasks/${id}/start`).json<CleaningTaskResponse>(),
      claimAndStart: (id: string) =>
        http.post(`api/v1/cleaning-tasks/${id}/claim`).json<CleaningTaskResponse>(),
      complete: (id: string, body: CompleteCleaningTaskRequest) =>
        http
          .post(`api/v1/cleaning-tasks/${id}/complete`, { json: body })
          .json<CleaningTaskResponse>(),
      reportDamage: (id: string, body: ReportDamageRequest) =>
        http.post(`api/v1/cleaning-tasks/${id}/damages`, { json: body }).json<unknown>(),
      reportMissingItem: (id: string, body: ReportMissingItemRequest) =>
        http.post(`api/v1/cleaning-tasks/${id}/missing-items`, { json: body }).json<unknown>(),
      acknowledgeDamage: (id: string, damageId: string) =>
        http.post(`api/v1/cleaning-tasks/${id}/damages/${damageId}/acknowledge`).json<unknown>(),
      uploadPhoto: (
        id: string,
        file: File,
        kind: 'ChecklistEvidence' | 'Damage' | 'BeforeAfter',
        checklistItemId?: string | null,
        damageReportId?: string | null,
      ) => {
        const form = new FormData();
        form.append('file', file);
        form.append('kind', kind);
        if (checklistItemId) form.append('checklistItemId', checklistItemId);
        if (damageReportId) form.append('damageReportId', damageReportId);
        return http
          .post(`api/v1/cleaning-tasks/${id}/photos`, { body: form })
          .json<CleaningPhotoResponse>();
      },
      downloadPhoto: (id: string, photoId: string) =>
        http.get(`api/v1/cleaning-tasks/${id}/photos/${photoId}/content`).blob(),
      deletePhoto: (id: string, photoId: string) =>
        http.delete(`api/v1/cleaning-tasks/${id}/photos/${photoId}`),
    },

    checklistTemplates: {
      getForProperty: (propertyId: string) =>
        http
          .get(`api/v1/checklist-templates/by-property/${propertyId}`)
          .json<ChecklistTemplateResponse>(),
      create: (body: CreateChecklistTemplateRequest) =>
        http.post('api/v1/checklist-templates', { json: body }).json<ChecklistTemplateResponse>(),
      update: (id: string, body: UpdateChecklistTemplateRequest) =>
        http
          .put(`api/v1/checklist-templates/${id}`, { json: body })
          .json<ChecklistTemplateResponse>(),
    },

    guests: {
      get: (id: string) => http.get(`api/v1/admin/guests/${id}`).json<GuestProfileResponse>(),
      downloadDocument: (guestProfileId: string, documentId: string) =>
        http.get(`api/v1/admin/guests/${guestProfileId}/documents/${documentId}/content`).blob(),
    },

    system: {
      auditLogs: (filter: AuditLogFilter = {}) => {
        const params = new URLSearchParams();
        if (filter.entityType) params.set('entityType', filter.entityType);
        if (filter.action) params.set('action', filter.action);
        if (filter.userId) params.set('userId', filter.userId);
        if (filter.from) params.set('from', filter.from);
        if (filter.to) params.set('to', filter.to);
        params.set('page', String(filter.page ?? 1));
        params.set('pageSize', String(filter.pageSize ?? 50));
        return http
          .get('api/v1/admin/audit-logs', { searchParams: params })
          .json<PagedResult<AuditLogResponse>>();
      },
      outboxList: (status: OutboxFilterStatus = 'All', page = 1, pageSize = 50) =>
        http
          .get('api/v1/admin/outbox', { searchParams: { status, page, pageSize } })
          .json<PagedResult<OutboxMessageResponse>>(),
      outboxRetry: (id: string) => http.post(`api/v1/admin/outbox/${id}/retry`).then(() => void 0),
      outboxRetryAllFailed: () =>
        http.post('api/v1/admin/outbox/retry-all-failed').json<{ affected: number }>(),
      onboardingLinks: (bookingId?: string, page = 1, pageSize = 50) => {
        const params = new URLSearchParams();
        if (bookingId) params.set('bookingId', bookingId);
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        return http
          .get('api/v1/admin/onboarding-links', { searchParams: params })
          .json<PagedResult<OnboardingLinkAdminResponse>>();
      },
      /** Buchungen, die eine Onboarding-Mail bekommen müssten, aber keine haben. */
      pendingOnboardings: () =>
        http.get('api/v1/admin/onboarding-links/pending').json<PendingOnboardingResponse[]>(),
      onboardingLinkReIssue: (bookingId: string) =>
        http
          .post('api/v1/admin/onboarding-links/re-issue', { json: { bookingId } })
          .json<OnboardingLinkAdminResponse>(),
      /**
       * Stellt einen frischen Link aus und liefert ihn EINMALIG; kein anderer Aufruf gibt ihn
       * heraus. Die URL nie in einen Cache oder Query-Key legen.
       */
      onboardingShareLink: (bookingId: string) =>
        http
          .post('api/v1/admin/onboarding-links/share-link', { json: { bookingId } })
          .json<OnboardingShareLinkResponse>(),
      onboardingDeliveryRetry: (deliveryId: string) =>
        http
          .post(`api/v1/admin/onboarding-links/deliveries/${deliveryId}/retry`)
          .then(() => void 0),
      guestConsents: (guestProfileId: string) =>
        http
          .get(`api/v1/admin/guests/${guestProfileId}/consents`)
          .json<ConsentLogAdminResponse[]>(),
    },

    propertyFiles: {
      tree: (propertyId: string) =>
        http.get(`api/v1/properties/${propertyId}/files/tree`).json<PropertyFileTreeNode>(),
      list: (propertyId: string, filter: PropertyFileListFilter = {}) => {
        const params = new URLSearchParams();
        if (filter.kind) params.set('kind', filter.kind);
        if (filter.year !== undefined) params.set('year', String(filter.year));
        if (filter.activeOnly !== undefined) params.set('activeOnly', String(filter.activeOnly));
        if (filter.expiringWithinDays !== undefined)
          params.set('expiringWithinDays', String(filter.expiringWithinDays));
        if (filter.counterparty) params.set('counterparty', filter.counterparty);
        return http
          .get(`api/v1/properties/${propertyId}/files`, { searchParams: params })
          .json<PropertyFileResponse[]>();
      },
      download: (propertyId: string, fileId: string) =>
        http.get(`api/v1/properties/${propertyId}/files/${fileId}/content`).blob(),
      /** Sammel-Download: die gewählten Dateien als ein ZIP (Ordnerstruktur erhalten). */
      downloadZip: (propertyId: string, ids: string[]) =>
        http
          .get(`api/v1/properties/${propertyId}/files/zip`, {
            searchParams: { ids: ids.join(',') },
          })
          .blob(),
      /**
       * Liefert ein Resized-Image (thumb=200px, medium=800px) als Blob. Fallback
       * auf Original wenn Variant nicht existiert.
       */
      downloadVariant: (
        propertyId: string,
        fileId: string,
        variant: 'thumb' | 'medium' | 'original',
      ) =>
        http
          .get(`api/v1/properties/${propertyId}/files/${fileId}/content`, {
            searchParams: { variant },
          })
          .blob(),
      upload: (propertyId: string, file: File, metadata: UploadPropertyFileRequest) => {
        const form = new FormData();
        form.append('file', file);
        form.append('metadata', JSON.stringify(metadata));
        return http
          .post(`api/v1/properties/${propertyId}/files`, { body: form })
          .json<PropertyFileResponse>();
      },
      updateMetadata: (
        propertyId: string,
        fileId: string,
        body: UpdatePropertyFileMetadataRequest,
      ) =>
        http
          .put(`api/v1/properties/${propertyId}/files/${fileId}/metadata`, { json: body })
          .json<PropertyFileResponse>(),
      delete: (propertyId: string, fileId: string) =>
        http.delete(`api/v1/properties/${propertyId}/files/${fileId}`).then(() => void 0),
      /** Verschiebt eine Datei innerhalb ihrer Kategorie (Placement, ändert nie den Kind). */
      move: (propertyId: string, fileId: string, body: MovePropertyFileRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/files/${fileId}/move`, { json: body })
          .json<PropertyFileResponse>(),
      /** Umklassifizieren: ändert den Kind + re-filet die Datei (einzige Kind-Änderung). */
      reclassify: (propertyId: string, fileId: string, body: ReclassifyPropertyFileRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/files/${fileId}/reclassify`, { json: body })
          .json<PropertyFileResponse>(),
      createFolder: (propertyId: string, body: CreateFolderRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/files/folders`, { json: body })
          .json<PropertyFolderResponse>(),
      renameFolder: (propertyId: string, body: RenameFolderRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/files/folders/rename`, { json: body })
          .json<PropertyFolderResponse>(),
      deleteFolder: (propertyId: string, path: string) =>
        http
          .delete(`api/v1/properties/${propertyId}/files/folders`, {
            searchParams: { path },
          })
          .then(() => void 0),
      /** Papierkorb-Inhalt (gelöschte, noch nicht gepurgte Dateien) dieser Property. */
      trash: (propertyId: string) =>
        http.get(`api/v1/properties/${propertyId}/files/trash`).json<TrashedFileResponse[]>(),
      /** Holt eine getrashte Datei an ihren Original-Ordner zurück. */
      restore: (propertyId: string, fileId: string) =>
        http
          .post(`api/v1/properties/${propertyId}/files/${fileId}/restore`)
          .json<PropertyFileResponse>(),
    },

    contracts: {
      list: (propertyId: string, filter: ContractListFilter = {}) => {
        const params = new URLSearchParams();
        if (filter.status) params.set('status', filter.status);
        if (filter.type) params.set('type', filter.type);
        if (filter.expiringWithinDays !== undefined) {
          params.set('expiringWithinDays', String(filter.expiringWithinDays));
        }
        return http
          .get(`api/v1/properties/${propertyId}/contracts`, { searchParams: params })
          .json<ContractResponse[]>();
      },
      create: (propertyId: string, file: File, metadata: CreateContractRequest) => {
        const form = new FormData();
        form.append('file', file);
        form.append('metadata', JSON.stringify(metadata));
        return http
          .post(`api/v1/properties/${propertyId}/contracts`, { body: form })
          .json<ContractResponse>();
      },
      update: (propertyId: string, id: string, body: UpdateContractRequest) =>
        http
          .put(`api/v1/properties/${propertyId}/contracts/${id}`, { json: body })
          .json<ContractResponse>(),
      cancel: (propertyId: string, id: string) =>
        http
          .post(`api/v1/properties/${propertyId}/contracts/${id}/cancel`)
          .json<ContractResponse>(),
      delete: (propertyId: string, id: string) =>
        http.delete(`api/v1/properties/${propertyId}/contracts/${id}`).then(() => void 0),
    },

    supplierInvoices: {
      list: (propertyId: string, filter: SupplierInvoiceListFilter = {}) => {
        const params = new URLSearchParams();
        if (filter.status) params.set('status', filter.status);
        if (filter.year !== undefined) params.set('year', String(filter.year));
        if (filter.supplier) params.set('supplier', filter.supplier);
        if (filter.category) params.set('category', filter.category);
        return http
          .get(`api/v1/properties/${propertyId}/supplier-invoices`, { searchParams: params })
          .json<SupplierInvoiceResponse[]>();
      },
      create: (propertyId: string, file: File, metadata: CreateSupplierInvoiceRequest) => {
        const form = new FormData();
        form.append('file', file);
        form.append('metadata', JSON.stringify(metadata));
        return http
          .post(`api/v1/properties/${propertyId}/supplier-invoices`, { body: form })
          .json<SupplierInvoiceResponse>();
      },
      update: (propertyId: string, id: string, body: UpdateSupplierInvoiceRequest) =>
        http
          .put(`api/v1/properties/${propertyId}/supplier-invoices/${id}`, { json: body })
          .json<SupplierInvoiceResponse>(),
      markPaid: (propertyId: string, id: string, body: MarkInvoicePaidRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/supplier-invoices/${id}/mark-paid`, {
            json: body,
          })
          .json<SupplierInvoiceResponse>(),
      markDisputed: (propertyId: string, id: string) =>
        http
          .post(`api/v1/properties/${propertyId}/supplier-invoices/${id}/mark-disputed`)
          .json<SupplierInvoiceResponse>(),
      delete: (propertyId: string, id: string) =>
        http.delete(`api/v1/properties/${propertyId}/supplier-invoices/${id}`).then(() => void 0),
    },

    finance: {
      overview: (query: FinanceOverviewQuery) => {
        const params = new URLSearchParams({ range: query.range });
        if (query.anchorDate) params.set('anchorDate', query.anchorDate);
        if (query.from) params.set('from', query.from);
        if (query.to) params.set('to', query.to);
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        return http
          .get('api/v1/finance/overview', { searchParams: params })
          .json<FinanceOverviewResponse>();
      },
      analytics: (query: FinanceAnalyticsQuery) => {
        const params = new URLSearchParams({ from: query.from, to: query.to });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        return http
          .get('api/v1/finance/analytics', { searchParams: params })
          .json<FinanceAnalyticsResponse>();
      },
      feeEvidence: (query: FinanceFeeEvidenceQuery) => {
        const params = new URLSearchParams({
          from: query.from,
          to: query.to,
          currency: query.currency,
          source: query.source,
          pageSize: String(query.pageSize ?? 25),
        });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        if (query.cursor) params.set('cursor', query.cursor);
        return http
          .get('api/v1/finance/analytics/fee-evidence', { searchParams: params })
          .json<FinanceFeeEvidenceFeed>();
      },
      feeEvidenceDetail: (publicReference: string, query: FinanceFeeEvidenceQuery) => {
        const params = new URLSearchParams({
          from: query.from,
          to: query.to,
          currency: query.currency,
          source: query.source,
        });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        return http
          .get(`api/v1/finance/analytics/fee-evidence/${encodeURIComponent(publicReference)}`, {
            searchParams: params,
          })
          .json<FinanceFeeEvidenceCard>();
      },
      portfolio: (query: PortfolioQuery) => {
        const params = new URLSearchParams({
          from: query.from,
          to: query.to,
          mode: query.mode,
          includeArchived: String(query.includeArchived),
        });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        return http
          .get('api/v1/finance/portfolio', { searchParams: params })
          .json<PortfolioResponse>();
      },
      monthly: (query: FinanceMonthlyQuery) => {
        const params = new URLSearchParams({ stayMonth: query.stayMonth });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        return http
          .get('api/v1/finance/monthly', { searchParams: params })
          .json<FinanceMonthlyResponse>();
      },
      bookings: (query: FinanceBookingQuery) => {
        const params = new URLSearchParams({
          from: query.from,
          to: query.to,
          pageSize: String(query.pageSize ?? 25),
        });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        if (query.currency) params.set('currency', query.currency);
        if (query.cursor) params.set('cursor', query.cursor);
        return http
          .get('api/v1/finance/bookings', { searchParams: params })
          .json<FinanceBookingFeed>();
      },
      booking: (
        publicReference: string,
        query: Pick<FinanceBookingQuery, 'propertyGroupIds' | 'propertyIds'>,
      ) => {
        const params = new URLSearchParams();
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        return http
          .get(`api/v1/finance/bookings/${encodeURIComponent(publicReference)}`, {
            searchParams: params,
          })
          .json<FinanceBookingDetail>();
      },
      createBookingManualDecision: (
        publicReference: string,
        body: CreateFinanceBookingManualDecisionRequest,
      ) =>
        http
          .post(
            `api/v1/admin/finance/bookings/${encodeURIComponent(publicReference)}/manual-decisions`,
            {
              json: body,
            },
          )
          .then(() => void 0),
      events: (query: FinanceJournalEventsQuery) => {
        const params = new URLSearchParams({
          occurredFrom: query.occurredFrom,
          occurredTo: query.occurredTo,
          currentOnly: String(query.currentOnly),
        });
        query.propertyGroupIds.forEach((id) => params.append('propertyGroupIds', id));
        query.propertyIds.forEach((id) => params.append('propertyIds', id));
        query.categories.forEach((category) => params.append('categories', category));
        query.financeStatuses.forEach((status) => params.append('financeStatuses', status));
        query.qualityCodes.forEach((code) => params.append('qualityCodes', code));
        if (query.stayFrom) params.set('stayFrom', query.stayFrom);
        if (query.stayTo) params.set('stayTo', query.stayTo);
        if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
        if (query.cursor) params.set('cursor', query.cursor);
        return http
          .get('api/v1/finance/events', { searchParams: params })
          .json<FinanceJournalEventsPage>();
      },
      event: (reference: string) =>
        http
          .get(`api/v1/finance/events/${encodeURIComponent(reference)}`)
          .json<FinanceJournalEventDetail>(),
      exportEvents: (query: FinanceJournalEventsQuery) => {
        const { pageSize: _, cursor: __, ...filter } = query;
        return http.post('api/v1/finance/events/export', { json: filter }).blob();
      },
      createManualDecision: (body: CreateFinancePositionManualDecisionRequest) =>
        http.post('api/v1/admin/finance/manual-decisions', { json: body }).then(() => void 0),
      completeOtaData: (body: CompleteOtaFinanceDataRequest) =>
        http
          .post('api/v1/admin/finance/manual-decisions/ota-data', { json: body })
          .then(() => void 0),
      /**
       * P&L fuer einen Zeitraum. period akzeptiert "2026" | "2026-Q1" | "2026-03" |
       * "2026-01-01..2026-06-30". Wenn period weggelassen, faellt der Backend auf year zurueck.
       */
      pnl: (propertyId: string, opts: { year?: number; period?: string } = {}) => {
        const params = buildPeriodParams(opts);
        return http
          .get(`api/v1/admin/finance/property/${propertyId}/pnl`, { searchParams: params })
          .json<PnLResponse>();
      },
      expenses: (propertyId: string, opts: { year?: number; period?: string } = {}) => {
        const params = buildPeriodParams(opts);
        return http
          .get(`api/v1/admin/finance/property/${propertyId}/expenses`, { searchParams: params })
          .json<ExpenseListItem[]>();
      },
      exportCsv: (year: number, propertyId?: string) => {
        const params: Record<string, string | number> = { year };
        if (propertyId) params.propertyId = propertyId;
        return http.get('api/v1/admin/finance/export/csv', { searchParams: params }).blob();
      },
    },

    insights: {
      /**
       * Operative + Forecast-KPIs (Occupancy/ADR/RevPAR/LoS/Channel-Mix/Pipeline).
       * Period-Format identisch zu finance.pnl: year | "2026-Q1" | "2026-03" | "2026-01-01..2026-06-30".
       */
      property: (propertyId: string, opts: { year?: number; period?: string } = {}) => {
        const params = buildPeriodParams(opts);
        return http
          .get(`api/v1/admin/insights/property/${propertyId}`, { searchParams: params })
          .json<InsightsResponse>();
      },
    },

    invoices: {
      /** Übersicht über alle Rechnungen des Workspace — globale Sicht, kein globaler Speicherort. */
      list: (query: InvoiceListQuery = {}) => {
        const params = new URLSearchParams();
        if (query.propertyId) params.set('propertyId', query.propertyId);
        if (query.from) params.set('from', query.from);
        if (query.to) params.set('to', query.to);
        if (query.status) params.set('status', query.status);
        if (query.limit !== undefined) params.set('limit', String(query.limit));
        return http
          .get('api/v1/admin/invoices', { searchParams: params })
          .json<InvoiceListItem[]>();
      },
      createForBooking: (bookingId: string) =>
        http.post(`api/v1/admin/bookings/${bookingId}/invoice`).json<InvoiceResponse>(),
      // Alle Rechnungen der Buchung — Beherbergung UND Parkplatz. Die frühere Einzel-Route
      // lieferte nur die Beherbergung; eine Parkplatzrechnung tauchte damit nirgends auf.
      listForBooking: (bookingId: string) =>
        http.get(`api/v1/admin/bookings/${bookingId}/invoices`).json<InvoiceResponse[]>(),
      createParkingForBooking: (bookingId: string) =>
        http.post(`api/v1/admin/bookings/${bookingId}/parking-invoice`).json<InvoiceResponse[]>(),
      downloadPdf: (invoiceId: string) => http.get(`api/v1/admin/invoices/${invoiceId}/pdf`).blob(),
      // Der ausdrueckliche Nachversand. Nimmt denselben Weg wie die Automatik nach dem Aufenthalt,
      // damit ein Probelauf auch wirklich den Weg prueft, den der Kunde bekommt. Antwort ist die
      // Zahl der angehaengten Belege.
      resendMail: (bookingId: string) =>
        http.post(`api/v1/admin/bookings/${bookingId}/invoice-mail`).json<number>(),
      // Der Builder. Ein Entwurf zieht erst beim Festschreiben eine Nummer.
      createDraft: (bookingId: string, body: CreateInvoiceDraftRequest) =>
        http
          .post(`api/v1/admin/bookings/${bookingId}/invoice-drafts`, { json: body })
          .json<InvoiceDraftResponse>(),
      getDraft: (draftId: string) =>
        http.get(`api/v1/admin/invoice-drafts/${draftId}`).json<InvoiceDraftResponse>(),
      updateDraft: (draftId: string, body: UpdateInvoiceDraftRequest) =>
        http
          .put(`api/v1/admin/invoice-drafts/${draftId}`, { json: body })
          .json<InvoiceDraftResponse>(),
      /** Das echte PDF mit ENTWURF-Wasserzeichen — wird serverseitig nirgends abgelegt. */
      previewDraft: (draftId: string) =>
        http.post(`api/v1/admin/invoice-drafts/${draftId}/preview`).blob(),
      issueDraft: (draftId: string) =>
        http.post(`api/v1/admin/invoice-drafts/${draftId}/issue`).json<InvoiceResponse>(),
      discardDraft: (draftId: string) =>
        http.delete(`api/v1/admin/invoice-drafts/${draftId}`).then(() => void 0),
      cancel: (invoiceId: string, reason: string | null) =>
        http
          .post(`api/v1/admin/invoices/${invoiceId}/cancel`, { json: { reason } })
          .json<InvoiceResponse>(),
      replaceRecipient: (invoiceId: string, body: InvoiceRecipientReplacementRequest) =>
        http
          .post(`api/v1/admin/invoices/${invoiceId}/recipient-replacement`, { json: body })
          .json<InvoiceResponse>(),
    },

    otaRefunds: {
      get: (bookingId: string) =>
        http.get(`api/v1/admin/bookings/${bookingId}/ota-refunds`).json<OtaBookingRefundLedger>(),
      create: (bookingId: string, body: CreateOtaBookingRefundRequest) =>
        http
          .post(`api/v1/admin/bookings/${bookingId}/ota-refunds`, { json: body })
          .json<OtaBookingRefundResponse>(),
      compensate: (bookingId: string, refundId: string, body: CompensateOtaBookingRefundRequest) =>
        http
          .post(`api/v1/admin/bookings/${bookingId}/ota-refunds/${refundId}/compensations`, {
            json: body,
          })
          .json<OtaBookingRefundResponse>(),
    },

    financialAutomation: {
      listCases: () =>
        http.get('api/v1/admin/finance/automation/cases').json<FinancialAutomationCaseResponse[]>(),
      listWork: () =>
        http.get('api/v1/admin/finance/automation/work').json<FinancialAutomationWorkResponse[]>(),
      listCutover: () =>
        http
          .get('api/v1/admin/finance/automation/cutover')
          .json<FinancialAutomationCutoverResponse[]>(),
      retry: (id: string) =>
        http.post(`api/v1/admin/finance/automation/work/${id}/retry`).then(() => void 0),
      appendDecision: (id: string, body: FinancialAutomationDecisionRequest) =>
        http
          .post(`api/v1/admin/finance/automation/cases/${id}/decision`, { json: body })
          .then(() => void 0),
      pause: (sourceKind: string) =>
        http
          .post(`api/v1/admin/finance/automation/cutover/${encodeURIComponent(sourceKind)}/pause`)
          .json<FinancialAutomationCutoverResponse>(),
    },

    messaging: {
      listBookingMessages: (bookingId: string, clientOperationId?: string) =>
        http
          .get(`api/v1/admin/messaging/bookings/${bookingId}/messages`, {
            searchParams: clientOperationId ? { clientOperationId } : undefined,
          })
          .json<BookingMessageOperationResponse[]>(),
      queueBookingMessage: (bookingId: string, body: { clientOperationId: string; body: string }) =>
        http
          .post(`api/v1/admin/messaging/bookings/${bookingId}/messages`, { json: body, retry: 0 })
          .json<BookingMessageOperationResponse>(),
      getSendOperationForMessage: (messageId: string) =>
        http
          .get(`api/v1/messaging/send-operations/by-message/${messageId}`)
          .json<MessageSendOperationDetail>(),
      resolveSend: (id: string, body: MessageSendResolutionRequest) =>
        http
          .post(`api/v1/messaging/send-operations/${id}/resolution`, { json: body })
          .json<MessageSendResolutionResponse>(),
      list: (opts?: {
        channel?: string;
        unreadOnly?: boolean;
        awaitingReplyOnly?: boolean;
        cursor?: string;
        take?: number;
        propertyIds?: string[] | null;
      }) => {
        const params = new URLSearchParams();
        if (opts?.channel) params.set('channel', opts.channel);
        if (opts?.unreadOnly) params.set('unreadOnly', 'true');
        if (opts?.awaitingReplyOnly) params.set('awaitingReplyOnly', 'true');
        if (opts?.cursor) params.set('cursor', opts.cursor);
        if (opts?.take !== undefined) params.set('take', String(opts.take));
        appendPropertyIds(params, opts?.propertyIds);
        return http
          .get('api/v1/admin/messaging/conversations', { searchParams: params })
          .json<CursorPage<ConversationListItem>>();
      },
      get: (id: string) =>
        http.get(`api/v1/admin/messaging/conversations/${id}`).json<ConversationDetail>(),
      listMessages: (id: string, opts?: { cursor?: string; take?: number }) => {
        const params: Record<string, string | number> = {};
        if (opts?.cursor) params.cursor = opts.cursor;
        if (opts?.take !== undefined) params.take = opts.take;
        return http
          .get(`api/v1/admin/messaging/conversations/${id}/messages`, { searchParams: params })
          .json<CursorPage<MessageItem>>();
      },
      markAsRead: (id: string) =>
        http.post(`api/v1/admin/messaging/conversations/${id}/mark-read`).then(() => void 0),
      unread: (propertyIds?: string[] | null) => {
        const params = new URLSearchParams();
        appendPropertyIds(params, propertyIds);
        return http
          .get('api/v1/admin/messaging/unread', { searchParams: params })
          .json<UnreadSummary>();
      },
      reply: (id: string, body: MessagingReplyRequest) =>
        http
          .post(`api/v1/admin/messaging/conversations/${id}/reply`, { json: body })
          .json<MessageItem>(),
      close: (id: string) =>
        http.post(`api/v1/admin/messaging/conversations/${id}/close`).then(() => void 0),
      noReplyNeeded: (id: string) =>
        http.post(`api/v1/admin/messaging/conversations/${id}/no-reply-needed`).then(() => void 0),
      setNote: (id: string, note: string | null) =>
        http
          .put(`api/v1/admin/messaging/conversations/${id}/note`, { json: { note } })
          .then(() => void 0),
      apartments: (id: string) =>
        http
          .get(`api/v1/admin/messaging/conversations/${id}/apartments`)
          .json<MessagingApartmentCard[]>(),
      // bookingId benennt das Apartment einer Multi-Room-Reservierung; ohne Auswahl antwortet der
      // Server dort mit 400 statt mit leeren Feldern.
      accessInfo: (id: string, bookingId?: string | null) =>
        http
          .get(`api/v1/admin/messaging/conversations/${id}/access-info`, {
            searchParams: bookingId ? { bookingId } : {},
          })
          .json<MessagingAccessInfo>(),
      // Attachment-Proxy: authentifizierter Blob-Fetch (ein <img src> wuerde den Bearer nicht mitschicken).
      getAttachment: (convId: string, msgId: string, attachmentId: string) =>
        http
          .get(
            `api/v1/admin/messaging/conversations/${convId}/messages/${msgId}/attachments/${attachmentId}`,
          )
          .blob(),
    },

    // Sprint 7 Issue 4: TenantSettings abgeschafft. Absender-Daten leben in invoiceTemplates,
    // MwSt-Saetze in taxSettings.
    taxSettings: {
      get: () => http.get('api/v1/admin/tax-settings').json<TaxSettingsResponse>(),
      update: (body: UpdateTaxSettingsRequest) =>
        http.put('api/v1/admin/tax-settings', { json: body }).json<TaxSettingsResponse>(),
    },

    stripe: {
      getSettings: () => http.get('api/v1/admin/stripe/settings').json<StripeSettingsResponse>(),
      updateSettings: (body: UpdateStripeSettingsRequest) =>
        http.put('api/v1/admin/stripe/settings', { json: body }).json<StripeSettingsResponse>(),
      runDoctor: () => http.post('api/v1/admin/stripe/doctor').json<StripeDoctorResult>(),
    },

    parking: {
      listSpots: (propertyGroupId: string) =>
        http
          .get(`api/v1/property-groups/${propertyGroupId}/parking-spots`)
          .json<ParkingSpotListResponse>(),
      createSpot: (propertyGroupId: string, body: SaveParkingSpotRequest) =>
        http
          .post(`api/v1/property-groups/${propertyGroupId}/parking-spots`, { json: body })
          .json<ParkingSpotResponse>(),
      updateSpot: (propertyGroupId: string, spotId: string, body: SaveParkingSpotRequest) =>
        http
          .put(`api/v1/property-groups/${propertyGroupId}/parking-spots/${spotId}`, { json: body })
          .json<ParkingSpotResponse>(),
      deleteSpot: (propertyGroupId: string, spotId: string) =>
        http
          .delete(`api/v1/property-groups/${propertyGroupId}/parking-spots/${spotId}`)
          .then(() => void 0),
      occupancy: (propertyGroupId: string, from: string, to: string) =>
        http
          .get(`api/v1/property-groups/${propertyGroupId}/parking-spots/occupancy`, {
            searchParams: { from, to },
          })
          .json<ParkingOccupancyResponse>(),
      createBlock: (propertyGroupId: string, body: CreateParkingBlockRequest) =>
        http
          .post(`api/v1/property-groups/${propertyGroupId}/parking-spots/blocks`, { json: body })
          .json<ParkingBatchResult<ParkingBlockResponse>>(),
      releaseBlock: (propertyGroupId: string, blockId: string) =>
        http
          .delete(`api/v1/property-groups/${propertyGroupId}/parking-spots/blocks/${blockId}`)
          .then(() => void 0),
      // Wem könnte dieser Zeitraum gehören? Die Gäste, die dann im Haus sind — sortiert nach
      // Überschneidung, statt eine leere Suchmaske anzubieten.
      bookingSuggestions: (propertyGroupId: string, from: string, to: string, search?: string) =>
        http
          .get(`api/v1/property-groups/${propertyGroupId}/parking-spots/booking-suggestions`, {
            searchParams: search?.trim() ? { from, to, search: search.trim() } : { from, to },
          })
          .json<ParkingBookingSuggestionsResponse>(),
      createMappedReservation: (
        propertyGroupId: string,
        body: CreateMappedParkingReservationRequest,
      ) =>
        http
          .post(`api/v1/property-groups/${propertyGroupId}/parking-spots/reservations`, {
            json: body,
          })
          .json<ParkingBatchResult<MappedParkingReservationResponse>>(),
    },

    resources: {
      list: (propertyGroupId: string) =>
        http
          .get(`api/v1/property-groups/${propertyGroupId}/resources`)
          .json<BookableResourceListResponse>(),
      create: (propertyGroupId: string, body: SaveBookableResourceRequest) =>
        http
          .post(`api/v1/property-groups/${propertyGroupId}/resources`, { json: body })
          .json<BookableResourceResponse>(),
      update: (propertyGroupId: string, resourceId: string, body: SaveBookableResourceRequest) =>
        http
          .put(`api/v1/property-groups/${propertyGroupId}/resources/${resourceId}`, { json: body })
          .json<BookableResourceResponse>(),
      /** Fotos für Extras & Erlebnisse; an Gäste gehen nur ihre Fassungen. */
      images: {
        list: (propertyGroupId: string, resourceId: string) =>
          http
            .get(`api/v1/property-groups/${propertyGroupId}/resources/${resourceId}/images`)
            .json<ResourceImageListResponse>(),
        upload: (propertyGroupId: string, resourceId: string, file: File) => {
          const body = new FormData();
          body.append('file', file);
          return http
            .post(`api/v1/property-groups/${propertyGroupId}/resources/${resourceId}/images`, {
              body,
            })
            .json<ResourceImageResponse>();
        },
        remove: (propertyGroupId: string, resourceId: string, imageId: string) =>
          http
            .delete(
              `api/v1/property-groups/${propertyGroupId}/resources/${resourceId}/images/${imageId}`,
            )
            .then(() => void 0),
        move: (
          propertyGroupId: string,
          resourceId: string,
          imageId: string,
          direction: 'Up' | 'Down',
        ) =>
          http
            .post(
              `api/v1/property-groups/${propertyGroupId}/resources/${resourceId}/images/${imageId}/move`,
              { json: { direction } },
            )
            .json<ResourceImageListResponse>(),
        content: (propertyGroupId: string, resourceId: string, imageId: string) =>
          http
            .get(
              `api/v1/property-groups/${propertyGroupId}/resources/${resourceId}/images/${imageId}/content`,
              { searchParams: { variant: 'thumb' } },
            )
            .blob(),
      },
      occupancy: (propertyGroupId: string, from: string, to: string) =>
        http
          .get(`api/v1/property-groups/${propertyGroupId}/resources/occupancy`, {
            searchParams: { from, to },
          })
          .json<ResourceOccupancyResponse>(),
      createBlock: (propertyGroupId: string, body: CreateResourceBlockRequest) =>
        http
          .post(`api/v1/property-groups/${propertyGroupId}/resources/blocks`, { json: body })
          .json<ResourceReservationResponse>(),
      /** `confirmPaid`: eine bezahlte Reservierung ohne Erstattung aufheben (ausdrücklich bestätigt). */
      cancelReservation: (propertyGroupId: string, reservationId: string, confirmPaid = false) =>
        http
          .delete(
            `api/v1/property-groups/${propertyGroupId}/resources/reservations/${reservationId}`,
            { searchParams: { confirmPaid } },
          )
          .json<ResourceReservationResponse>(),
    },

    // Sprint-6 C.1: Multi-Invoice-Template-API.
    invoiceTemplates: {
      list: () => http.get('api/v1/admin/invoice-templates').json<InvoiceTemplateResponse[]>(),
      create: (body: CreateInvoiceTemplateRequest) =>
        http.post('api/v1/admin/invoice-templates', { json: body }).json<InvoiceTemplateResponse>(),
      update: (id: string, body: UpdateInvoiceTemplateRequest) =>
        http
          .put(`api/v1/admin/invoice-templates/${id}`, { json: body })
          .json<InvoiceTemplateResponse>(),
      delete: (id: string) =>
        http.delete(`api/v1/admin/invoice-templates/${id}`).then(() => void 0),
      setPrimary: (id: string) =>
        http
          .post(`api/v1/admin/invoice-templates/${id}/set-primary`)
          .json<InvoiceTemplateResponse>(),
      uploadLogo: (id: string, file: File) => {
        const form = new FormData();
        form.append('file', file);
        return http
          .post(`api/v1/admin/invoice-templates/${id}/logo`, { body: form })
          .json<InvoiceTemplateResponse>();
      },
      logoUrl: (id: string) => `api/v1/admin/invoice-templates/${id}/logo`,
      removeLogo: (id: string) =>
        http.delete(`api/v1/admin/invoice-templates/${id}/logo`).then(() => void 0),
      assignToProperty: (propertyId: string, body: AssignInvoiceTemplateRequest) =>
        http
          .put(`api/v1/properties/${propertyId}/invoice-template`, { json: body })
          .then(() => void 0),
    },

    // Sprint-6/8 B.3: Admin-Dashboard-KPIs + Revenue-Trend.
    dashboard: {
      kpis: (propertyIds?: string[] | null) => {
        const params = new URLSearchParams();
        appendPropertyIds(params, propertyIds);
        return http
          .get('api/v1/admin/dashboard/kpis', { searchParams: params })
          .json<DashboardKpisResponse>();
      },
      revenueTrend: (days = 30, propertyIds?: string[] | null) => {
        const params = new URLSearchParams({ days: String(days) });
        appendPropertyIds(params, propertyIds);
        return http
          .get('api/v1/admin/dashboard/revenue-trend', { searchParams: params })
          .json<RevenueTrendResponse>();
      },
    },

    // Sprint-6 E.1: Per-Property Rechnungsbausteine.
    invoiceBlocks: {
      list: (propertyId: string) =>
        http.get(`api/v1/properties/${propertyId}/invoice-blocks`).json<InvoiceBlockResponse[]>(),
      create: (propertyId: string, body: CreateInvoiceBlockRequest) =>
        http
          .post(`api/v1/properties/${propertyId}/invoice-blocks`, { json: body })
          .json<InvoiceBlockResponse>(),
      update: (propertyId: string, blockId: string, body: UpdateInvoiceBlockRequest) =>
        http
          .put(`api/v1/properties/${propertyId}/invoice-blocks/${blockId}`, { json: body })
          .json<InvoiceBlockResponse>(),
      delete: (propertyId: string, blockId: string) =>
        http.delete(`api/v1/properties/${propertyId}/invoice-blocks/${blockId}`).then(() => void 0),
      reorder: (propertyId: string, body: ReorderInvoiceBlocksRequest) =>
        http
          .put(`api/v1/properties/${propertyId}/invoice-blocks/reorder`, { json: body })
          .json<InvoiceBlockResponse[]>(),
    },

    // Positions-Katalog für den Rechnungs-Builder — Workspace-weit, von Hand gewählt.
    invoiceItemPresets: {
      list: (activeOnly = false) =>
        http
          .get('api/v1/admin/invoice-item-presets', {
            searchParams: new URLSearchParams({ activeOnly: String(activeOnly) }),
          })
          .json<InvoiceItemPresetResponse[]>(),
      create: (body: CreateInvoiceItemPresetRequest) =>
        http
          .post('api/v1/admin/invoice-item-presets', { json: body })
          .json<InvoiceItemPresetResponse>(),
      update: (id: string, body: UpdateInvoiceItemPresetRequest) =>
        http
          .put(`api/v1/admin/invoice-item-presets/${id}`, { json: body })
          .json<InvoiceItemPresetResponse>(),
      delete: (id: string) =>
        http.delete(`api/v1/admin/invoice-item-presets/${id}`).then(() => void 0),
      reorder: (body: ReorderInvoiceItemPresetsRequest) =>
        http
          .put('api/v1/admin/invoice-item-presets/reorder', { json: body })
          .json<InvoiceItemPresetResponse[]>(),
    },
  };
}

export type Api = ReturnType<typeof buildApi>;
